import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
    FiArrowLeft,
    FiCalendar,
    FiClock,
    FiUsers,
    FiStar,
    FiMapPin,
    FiAward,
    FiCreditCard,
    FiChevronDown,
    FiChevronRight,
    FiAlertCircle,
    FiHeart,
    FiShare2,
    FiCheck,
    FiExternalLink,
    FiPackage,
    FiPlus,
} from 'react-icons/fi';
import './PackagesPage.css';
import Navbar from '../../../Components/Navber';
import api from "../../../Api"

// --- Package Type Selector Component ---
const PackageTypeSelector = ({ selectedType, onTypeSelect, packageTypes }) => {
    return (
        <div className="pkg-type-selector">
            <div className="pkg-type-scroll">
                {packageTypes.map((type) => (
                    <button
                        key={type.id}
                        className={`pkg-type-btn ${selectedType === type.id ? 'pkg-active' : ''}`}
                        onClick={() => onTypeSelect(type.id)}
                    >
                        <type.icon className="pkg-type-icon" />
                        <div className="pkg-type-info">
                            <span className="pkg-type-label">{type.label}</span>
                            <span className="pkg-type-desc">{type.description}</span>
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
};

// --- Package Card Component ---
const PackageCard = ({ packageData, destinationName, onBookNow }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const {
        id,
        name,
        slug,
        short_description,
        featured_photo_url,
        duration_days,
        duration_nights,
        gst_percentage,
        meal_plans_starting_from,
    } = packageData;

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0,
        }).format(price);
    };

    const getPackageType = () => {
        const gst = parseFloat(gst_percentage) || 0;
        if (gst > 30) return 'premium';
        if (gst > 15) return 'standard';
        return 'basic';
    };

    const packageType = getPackageType();
    const typeColors = {
        basic: 'pkg-badge-gold-light',
        standard: 'pkg-badge-gold',
        premium: 'pkg-badge-gold-dark',
    };

    return (
        <div
            className={`pkg-card ${isHovered ? 'pkg-hovered' : ''} ${isExpanded ? 'pkg-expanded' : ''}`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className="pkg-card-image">
                <img
                    src={featured_photo_url || 'https://via.placeholder.com/400x300?text=Package'}
                    alt={name}
                    loading="lazy"
                />
                <div className={`pkg-type-badge ${typeColors[packageType]}`}>
                    {packageType.charAt(0).toUpperCase() + packageType.slice(1)}
                </div>
                <div className="pkg-card-overlay">
                    <button
                        className="pkg-btn-view-details"
                        onClick={() => setIsExpanded(!isExpanded)}
                    >
                        {isExpanded ? 'Show Less' : 'Quick View'}
                    </button>
                </div>
            </div>

            <div className="pkg-card-content">
                <div className="pkg-card-header">
                    <h3 className="pkg-card-title">{name}</h3>
                    {/* <div className="pkg-card-actions">
                        <button className="pkg-icon-btn" aria-label="Share">
                            <FiShare2 />
                        </button>
                        <button className="pkg-icon-btn" aria-label="Save">
                            <FiHeart />
                        </button>
                    </div> */}
                </div>

                <p className="pkg-card-description">
                    {short_description || 'No description available'}
                </p>

                <div className="pkg-card-meta">
                    <div className="pkg-meta-item">
                        <FiCalendar />
                        <span>{duration_days} Days</span>
                    </div>
                    <div className="pkg-meta-item">
                        <FiClock />
                        <span>{duration_nights} Nights</span>
                    </div>
                    <div className="pkg-meta-item">
                        <FiUsers />
                        <span>Up to 200 Guests</span>
                    </div>
                </div>

                <div className="pkg-card-footer">
                    <div className="pkg-price">
                        <span className="pkg-price-label">Starting from</span>
                        <span className="pkg-price-amount">{formatPrice(meal_plans_starting_from)}</span>
                        <span className="pkg-price-gst">+ GST {gst_percentage}%</span>
                    </div>
                    <button
                        className="pkg-btn-book-now"
                        onClick={() => onBookNow(packageData)}
                    >
                        Book Now
                        <FiChevronRight />
                    </button>
                </div>

                {isExpanded && (
                    <div className="pkg-card-expanded">
                        <div className="pkg-expanded-details">
                            <h4>Package Includes</h4>
                            <ul>
                                <li><FiCheck /> Accommodation for {duration_nights} nights</li>
                                <li><FiCheck /> All meals as per meal plan</li>
                                <li><FiCheck /> Wedding venue decoration</li>
                                <li><FiCheck /> Photography & videography</li>
                                <li><FiCheck /> Entertainment & music</li>
                                <li><FiCheck /> Guest transportation</li>
                            </ul>
                            <div className="pkg-expanded-actions">
                                <Link to={`/destination-wedding-packages-details/${slug}`} className="pkg-btn-view-full">
                                    View Full Details
                                    <FiExternalLink />
                                </Link>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- Main Packages Page Component ---
export default function PackagesPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [destination, setDestination] = useState(null);
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedType, setSelectedType] = useState('all');

    const packageTypes = [
        { id: 'all', label: 'All', icon: FiPackage, description: 'View all packages' },
        { id: 'basic', label: 'Basic', icon: FiPackage, description: 'Essential packages' },
        { id: 'standard', label: 'Standard', icon: FiStar, description: 'Premium experiences' },
        { id: 'premium', label: 'Premium', icon: FiAward, description: 'Luxury & exclusive' },
    ];

    useEffect(() => {
        const fetchDestination = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await api.get(
                    `/wedding/destinations/${id}`
                );
                // if (!response.ok) {
                //     throw new Error(`HTTP error! status: ${response.status}`);
                // }
                const result = await response.data;
                if (result.status && result.data) {
                    setDestination(result.data);
                    setPackages(result.data.packages || []);
                } else {
                    throw new Error(result.message || 'Failed to fetch destination data');
                }
            } catch (err) {
                setError(err.message);
                setPackages([]);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchDestination();
        }
    }, [id]);

    const handleBookNow = (pkg) => {
        console.log('Booking package:', pkg);
        navigate(`/destination-wedding-packages-details/${pkg.slug}`);
    };

    const handleGoBack = () => {
        navigate('/wedding-destinations');
    };

    const getPackageType = (pkg) => {
        const gst = parseFloat(pkg.gst_percentage) || 0;
        if (gst > 30) return 'premium';
        if (gst > 15) return 'standard';
        return 'basic';
    };

    const filteredPackages = packages.filter((pkg) => {
        if (selectedType === 'all') return true;
        return getPackageType(pkg) === selectedType;
    });

    if (loading) {
        return (
            <div className="pkg-page-wrapper">
                <div className="pkg-hero-skeleton">
                    <div className="pkg-skeleton-banner"></div>
                </div>
                <div className="pkg-container">
                    <div className="pkg-loading">
                        <div className="pkg-skeleton-title"></div>
                        <div className="pkg-skeleton-text"></div>
                        <div className="pkg-grid-skeleton">
                            {[...Array(3)].map((_, i) => (
                                <div key={i} className="pkg-skeleton-card"></div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !destination) {
        return (
            <div className="pkg-page-wrapper">
                <div className="pkg-container">
                    <div className="pkg-empty-state">
                        <FiAlertCircle className="pkg-empty-icon" />
                        <h3>Destination Not Found</h3>
                        <p>{error || 'The destination you are looking for does not exist.'}</p>
                        <button onClick={handleGoBack} className="pkg-btn-primary">
                            <FiArrowLeft />
                            Back to Destinations
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const locationName =
        destination.city && destination.state
            ? `${destination.city.name}, ${destination.state.name}`
            : destination.state?.name || destination.city?.name || '';

    return (
        <div className="pkg-page-wrapper">
            <Navbar />
            {/* Hero Section */}
            <section className="pkg-hero">
                <div
                    className="pkg-hero-bg"
                    style={{
                        backgroundImage: `url(${destination.banner_url || destination.featured_photo_url})`,
                    }}
                >
                    <div className="pkg-hero-overlay"></div>
                </div>
                <div className="pkg-hero-content">
                    <div className="pkg-container">
                        <button className="pkg-btn-back" onClick={handleGoBack}>
                            <FiArrowLeft />
                            Back to Destinations
                        </button>
                        <div className="pkg-hero-text">
                            <div className="pkg-hero-badge">
                                <FiMapPin />
                                <span>{locationName}</span>
                            </div>
                            <h1 className="pkg-hero-title">{destination.name}</h1>
                           
                            <div className="pkg-hero-stats">
                                <div className="pkg-stat-item">
                                    <span className="pkg-stat-number">{packages.length}</span>
                                    <span className="pkg-stat-label">Packages</span>
                                </div>
                                <div className="pkg-stat-item">
                                    <span className="pkg-stat-number">{destination.city?.name || '—'}</span>
                                    <span className="pkg-stat-label">Location</span>
                                </div>
                                <div className="pkg-stat-item">
                                    <span className="pkg-stat-number">
                                        {packages.length > 0
                                            ? `₹${Math.min(
                                                ...packages.map((p) => p.meal_plans_starting_from || 0)
                                            ).toLocaleString()}`
                                            : '—'}
                                    </span>
                                    <span className="pkg-stat-label">Starting Price</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Packages Section */}
            <section className="pkg-section">
                <div className="pkg-container">
                    <div className="pkg-header">
                        <div className="pkg-header-left">
                            <h2 className="pkg-section-title">
                                Wedding Packages
                                <span className="pkg-title-count">{packages.length}</span>
                            </h2>
                            <p className="pkg-section-subtitle">
                                {destination.short_description ||
                                    destination.description?.replace(/<[^>]*>/g, '') ||
                                    'Experience the perfect wedding destination'}
                            </p>
                            <p className="pkg-section-subtitle">
                                Choose the perfect wedding package for your dream celebration at{' '}
                                {destination.name}
                            </p>
                        </div>
                    </div>

                    <PackageTypeSelector
                        selectedType={selectedType}
                        onTypeSelect={setSelectedType}
                        packageTypes={packageTypes}
                    />

                    {filteredPackages.length === 0 ? (
                        <div className="pkg-empty-state">
                            <FiAlertCircle className="pkg-empty-icon" />
                            <h3>No Packages Found</h3>
                            <p>No packages match your selected filter. Try adjusting your filter.</p>
                        </div>
                    ) : (
                        <div className="pkg-grid">
                            {filteredPackages.map((pkg) => (
                                <PackageCard
                                    key={pkg.id}
                                    packageData={pkg}
                                    destinationName={destination.name}
                                    onBookNow={handleBookNow}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Destination Highlights */}
            <section className="pkg-highlights">
                <div className="pkg-container">
                    <div className="pkg-highlights-grid">
                        <div className="pkg-highlight-item">
                            <div className="pkg-highlight-icon">
                                <FiAward />
                            </div>
                            <h4>Premium Venues</h4>
                            <p>Handpicked luxury venues with stunning views and world-class amenities</p>
                        </div>
                        <div className="pkg-highlight-item">
                            <div className="pkg-highlight-icon">
                                <FiUsers />
                            </div>
                            <h4>Expert Planners</h4>
                            <p>Dedicated wedding planners to handle every detail of your special day</p>
                        </div>
                        <div className="pkg-highlight-item">
                            <div className="pkg-highlight-icon">
                                <FiCalendar />
                            </div>
                            <h4>Flexible Dates</h4>
                            <p>Choose from multiple dates with customized packages to suit your needs</p>
                        </div>
                        <div className="pkg-highlight-item">
                            <div className="pkg-highlight-icon">
                                <FiCreditCard />
                            </div>
                            <h4>Easy Payments</h4>
                            <p>Flexible payment plans and secure booking with our trusted platform</p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}